import React, { useState } from 'react';
import { CardData } from '../types';

export interface CardProps extends CardData {
  isSelected?: boolean;
  onSelect?: () => void;
  onViewOnMap?: () => void;
}

// Clean helper for raw database placeholders like "-", "--", "N/A"
const sanitizeValue = (val?: string): string => {
  if (!val) return '';
  const trimmed = val.trim();
  if (trimmed === '-' || trimmed === '--' || trimmed === 'N/A' || trimmed === 'na') return '';
  return trimmed;
};

export const Card: React.FC<CardProps> = ({
  name,
  contact,
  area,
  fullAddress,
  isCateringServiceAvailable,
  businessName,
  category,
  imageUrl,
  isSelected = false,
  onSelect,
}) => {
  const [isHoveredAddress, setIsHoveredAddress] = useState(false);

  // Sanitize fullAddress & area so placeholders like "-" don't show up as "📍 -"
  const validFull = sanitizeValue(fullAddress);
  const validArea = sanitizeValue(area);
  const displayAddress = validFull || validArea || 'Bengaluru, Karnataka';

  const validBusiness = sanitizeValue(businessName);
  const validName = sanitizeValue(name);
  const displayName = validBusiness || validName || 'Gujarati Member';
  const secondaryName = validBusiness && validName && validBusiness !== validName ? validName : null;

  // Split address to get the first line
  const getAddressFirstLine = (addr: string) => {
    if (!addr) return '';
    if (addr.includes('\n')) {
      return addr.split('\n')[0].trim();
    }
    const parts = addr.split(',');
    if (parts.length > 1) {
      let first = parts[0].trim();
      // If very short (under 12 chars) and next part exists, combine
      if (first.length < 12 && parts.length > 2) {
        first = `${first}, ${parts[1].trim()}`;
      }
      return first;
    }
    return addr;
  };

  const firstLineAddress = getAddressFirstLine(displayAddress);

  // Helper to extract clean phone numbers (handles multiple numbers like "80957 28735, 76762 37875")
  const parsePhoneNumbers = (phoneStr?: string) => {
    if (!phoneStr) return [];
    const parts = phoneStr.split(/[,/;|]+/);
    return parts
      .map(p => p.trim())
      .filter(p => p.length > 0 && p !== '-' && p !== '--' && p !== 'N/A');
  };

  const phoneNumbers = parsePhoneNumbers(contact);
  const primaryPhone = phoneNumbers[0] || '';

  // Get primary category
  const validCategory = sanitizeValue(category);
  const primaryCategory = validCategory ? validCategory.split(',')[0].trim() : 'Food Services';

  return (
    <div
      onClick={onSelect}
      className={`group relative rounded-2xl p-3.5 sm:p-4 border transition-all duration-200 cursor-pointer flex items-center justify-between gap-3 sm:gap-4 ${
        isSelected
          ? 'bg-gradient-to-r from-[#FFF5ED] via-[#FFF9F2] to-[#FFF0E6] dark:from-orange-950/40 dark:via-gray-800/90 dark:to-gray-800/95 border-orange-300 dark:border-orange-500/60 shadow-md shadow-orange-500/5'
          : 'bg-[#FFFDFB] dark:bg-gray-800/95 border-[#F5ECE1] dark:border-gray-700/70 shadow-soft hover:shadow-soft-lg hover:border-orange-200 dark:hover:border-orange-800/60'
      }`}
    >
      {/* Left Thumbnail Image */}
      <div className="relative flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden bg-orange-100/60 dark:bg-gray-700 flex items-center justify-center border border-orange-100/80 dark:border-gray-700 shadow-sm">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={displayName}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-center p-2 w-full h-full bg-gradient-to-br from-amber-50 to-orange-100 dark:from-gray-800 dark:to-gray-700">
            <span className="w-10 h-10 rounded-full bg-orange-500 text-white font-bold text-lg flex items-center justify-center shadow-sm">
              {(displayName || 'G').charAt(0).toUpperCase()}
            </span>
            <span className="text-[10px] font-semibold text-orange-700 dark:text-orange-300 mt-1 truncate max-w-[70px]">
              {primaryCategory}
            </span>
          </div>
        )}
      </div>

      {/* Middle Content */}
      <div className="flex-1 min-w-0 flex flex-col justify-center space-y-1.5 py-0.5">
        {/* Title */}
        <div className="flex items-center gap-2">
          <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
            {displayName}
          </h3>
        </div>

        {/* Category Badge & Secondary Owner */}
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-[#FFF0E5] dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 border border-orange-200/50 dark:border-orange-900/40">
            {primaryCategory}
          </span>
          {secondaryName && (
            <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
              ({secondaryName})
            </span>
          )}
        </div>

        {/* Address: Only first line by default, full address on hover */}
        <div
          className="relative inline-block"
          onMouseEnter={() => setIsHoveredAddress(true)}
          onMouseLeave={() => setIsHoveredAddress(false)}
        >
          <div
            className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 transition-colors hover:text-orange-600 dark:hover:text-orange-400"
            title={displayAddress}
          >
            <span className="text-orange-500 flex-shrink-0 text-xs">📍</span>
            <span className="truncate max-w-[220px] sm:max-w-[300px] border-b border-dotted border-gray-300 dark:border-gray-600">
              {firstLineAddress}
            </span>
          </div>

          {/* Full Address Hover Tooltip / Popover */}
          {isHoveredAddress && (
            <div className="absolute left-0 bottom-full mb-2 z-50 w-64 sm:w-72 p-3 bg-gray-900/95 dark:bg-black/95 text-white text-xs rounded-xl shadow-soft-xl border border-gray-700 backdrop-blur-md pointer-events-none animate-in fade-in duration-150">
              <div className="flex items-center gap-1 text-orange-400 font-bold mb-1">
                <span>📍</span>
                <span>Full Address:</span>
              </div>
              <p className="text-gray-200 leading-relaxed font-normal">
                {displayAddress}
              </p>
              {/* Tooltip beak */}
              <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-900/95" />
            </div>
          )}
        </div>

        {/* Contact & Catering Available Badge */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs pt-0.5">
          {primaryPhone && (
            <a
              href={`tel:${primaryPhone.replace(/\D/g, '')}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 font-medium transition-colors"
            >
              <span className="text-gray-400">📞</span>
              <span className="truncate">{phoneNumbers.join(', ')}</span>
            </a>
          )}

          {isCateringServiceAvailable === 'Yes' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#EDF8F1] dark:bg-emerald-950/40 text-[#16A34A] dark:text-emerald-300 font-medium text-[11px] border border-emerald-200/50 dark:border-emerald-900/40">
              <span>🍽️</span>
              <span>Catering Available</span>
            </span>
          )}
        </div>
      </div>

      {/* Right Side: Circular Chevron Button (matching screenshot) */}
      <div className="flex-shrink-0 flex items-center">
        <div
          className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm ${
            isSelected
              ? 'bg-orange-500 text-white shadow-orange-200 dark:shadow-none'
              : 'bg-orange-100/70 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400 group-hover:bg-orange-500 group-hover:text-white'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default Card;